package com.shadowings.gero.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.shadowings.gero.data.datasource.chapterEight
import com.shadowings.gero.data.datasource.chapterEleven
import com.shadowings.gero.data.datasource.chapterFive
import com.shadowings.gero.data.datasource.chapterFour
import com.shadowings.gero.data.datasource.chapterNine
import com.shadowings.gero.data.datasource.chapterOne
import com.shadowings.gero.data.datasource.chapterSeven
import com.shadowings.gero.data.datasource.chapterSix
import com.shadowings.gero.data.datasource.chapterTen
import com.shadowings.gero.data.datasource.chapterFifteen
import com.shadowings.gero.data.datasource.chapterSixteen
import com.shadowings.gero.data.datasource.chapterFourteen
import com.shadowings.gero.data.datasource.chapterThirteen
import com.shadowings.gero.data.datasource.chapterThree
import com.shadowings.gero.data.datasource.chapterTwelve
import com.shadowings.gero.data.datasource.chapterTwo
import com.shadowings.gero.domain.model.Chapter
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.repository.AnalyticsRepository
import com.shadowings.gero.domain.repository.AuthRepository
import com.shadowings.gero.domain.repository.ChapterRepository
import com.shadowings.gero.domain.repository.DatabaseRepository
import com.shadowings.gero.domain.repository.UserDataRepository
import com.shadowings.gero.presentation.model.LandingUiState
import com.shadowings.gero.presentation.model.UiLessonNode
import com.shadowings.gero.presentation.model.UiLessonRow
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class LandingViewModel(
    private val chapterRepository: ChapterRepository,
    private val userDataRepository: UserDataRepository,
    databaseRepository: DatabaseRepository,
    private val authRepository: AuthRepository,
    private val analyticsRepository: AnalyticsRepository,
) : ViewModel() {

    private val _chapters = MutableStateFlow<List<Chapter>>(emptyList())
    val starsNeeded = MutableStateFlow<Map<String, Int>>(mapOf())

    val uiState: StateFlow<LandingUiState> = combine(
        _chapters,
        databaseRepository.isReady,
        userDataRepository.userData,
        authRepository.currentUser
    ) { chapters, _, userData, currentUser ->
        LandingUiState(
            userData = userData,
            hieros = databaseRepository.hieros,
            chapters = chapters,
            words = databaseRepository.words,
            isLoggedIn = currentUser != null,
            lessonRowsPerChapter = chapters.associate { chapter ->
                chapter.number to calculateLessonRows(chapter.number, userData)
            }
        )
    }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000), LandingUiState())

    init {
        viewModelScope.launch(Dispatchers.Default) {
            _chapters.value = chapterRepository.getChapters()
        }
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.userData.collect { userData ->
                val completedLessons = userData.stars.count { it.value > 0 }
                analyticsRepository.setUserProperty("lessons_completed", completedLessons.toString())
            }
        }
    }

    fun completeWalkthrough() {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setWalkthroughCompleted()
        }
    }

    fun completeTabWalkthrough(tabIndex: Int) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setTabWalkthroughCompleted(tabIndex)
        }
    }

    fun toggleFavoriteHiero(code: String) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.toggleFavoriteHiero(code)
        }
    }

    fun toggleFavoriteWord(id: String) {
        viewModelScope.launch(Dispatchers.Default) {
            val currentlyFavorite = uiState.value.userData.favoriteWords.contains(id)
            userDataRepository.toggleFavoriteWord(id)
            analyticsRepository.logFavoriteToggled(id, !currentlyFavorite)
        }
    }

    fun logStatClicked(statType: String) {
        analyticsRepository.logStatClicked(statType)
    }

    // Ordine bottom bar: 0 Impara, 1 Compendio, 2 Builder, 3 Personaggi, 4 Profilo.
    fun onBottomBarClick(index: Int) {
        when (index) {
            1 -> analyticsRepository.logClickCompendio()
            2 -> analyticsRepository.logClickBuilder()
            3 -> analyticsRepository.logClickPersonaggi()
            4 -> analyticsRepository.logClickProfilo()
        }
    }

    // Tab di "Impara": 0 Capitoli, 1 Correggi.
    fun onLearnTabClick(index: Int) {
        if (index == 1) analyticsRepository.logClickCorreggi()
    }

    fun onCompendioTabClick(index: Int) {
        when (index) {
            0 -> analyticsRepository.logTabSimboli()
            1 -> analyticsRepository.logTabParole()
            2 -> analyticsRepository.logTabRegole()
            3 -> analyticsRepository.logTabImmagini()
        }
    }

    fun redeemStreakRestoration(dateStr: String) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.redeemStreakRestorationToken(dateStr)
        }
    }

    fun acknowledgeStreakRestorationBadge() {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.setStreakRestorationBadgeSeen(true)
        }
    }

    fun logWordViewed(wordId: String, meaning: String) {
        analyticsRepository.logWordViewed(wordId, meaning)
    }

    fun logHieroViewed(hieroId: String, unicode: String) {
        analyticsRepository.logHieroViewed(hieroId, unicode)
    }

    fun saveBuilderWord(word: com.shadowings.gero.domain.model.SavedBuilderWord) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.saveBuilderWord(word)
        }
    }

    fun deleteBuilderWord(id: String) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.deleteBuilderWord(id)
        }
    }

    fun updateBuilderWord(word: com.shadowings.gero.domain.model.SavedBuilderWord) {
        viewModelScope.launch(Dispatchers.Default) {
            userDataRepository.updateBuilderWord(word)
        }
    }

    val allLessons =
        chapterOne + chapterTwo + chapterThree + chapterFour + chapterFive + chapterSix + chapterSeven + chapterEight + chapterNine + chapterTen + chapterEleven + chapterTwelve + chapterThirteen + chapterFourteen + chapterFifteen + chapterSixteen


    fun calculateLessonRows(chapterNumber: Int, userData: UserData): List<UiLessonRow> {
        val totalStars = userData.stars.values.sum()

        val chapterLessons = when (chapterNumber) {
            1 -> chapterOne
            2 -> chapterTwo
            3 -> chapterThree
            4 -> chapterFour
            5 -> chapterFive
            6 -> chapterSix
            7 -> chapterSeven
            8 -> chapterEight
            9 -> chapterNine
            10 -> chapterTen
            11 -> chapterEleven
            12 -> chapterTwelve
            13 -> chapterThirteen
            14 -> chapterFourteen
            15 -> chapterFifteen
            16 -> chapterSixteen
            else -> emptyList()
        }

        if (chapterLessons.isEmpty()) return emptyList()


        val list = chapterLessons.map { lesson ->

            val currentLessonIndex = allLessons.indexOf(lesson)

            val neededStars = (currentLessonIndex * 2.5).toInt() + (chapterNumber - 1) * 3

            starsNeeded.update {
                val newMap = it.toMutableMap()
                newMap[lesson.id] = neededStars
                newMap
            }

            val notEnoughTotalStars = totalStars < neededStars

            val allLessonsUntilNow = allLessons.subList(0, currentLessonIndex)
            val previousQuizzesDone = allLessonsUntilNow.all { (userData.stars[it.id] ?: 0) > 0 }
            val isRowLocked = notEnoughTotalStars || !previousQuizzesDone

            UiLessonRow(
                lessons = listOf(
                    UiLessonNode(
                        lesson = lesson,
                        isLocked = isRowLocked,
                        earnedStars = userData.stars[lesson.id] ?: 0,
                        isPerfect = userData.perfect.contains(lesson.id)
                    )
                )
            )
        }

        return list
    }

}